/**
 * Kalendář helpers — date math, URL parsing, event tones.
 *
 * Items that show on the calendar:
 *   - `delivery` — `vozidla_smlouva` předání: on the handover protocol's date
 *                 once handed over ("Předáno"), else the planned datum_predani
 *   - `task` — open úkoly pinned to `datum_pripominky`
 *   - `plan` — active planned contacts (`kontakt_plany`) pinned to `datum_planu`
 *   - `termin` — used-car STK / servisní prohlídka deadline pinned to its date;
 *                "Naplánováno na …" when servis already has it booked
 *
 * One tone per kind, from the locked palette: deliveries `ok` (green — a
 * customer milestone), planned contacts `accent` (blue), tasks `warn`
 * (orange), and STK / prohlídka deadlines: `danger` (red — needs booking)
 * when overdue or due within TERMIN_URGENT_DNU days, `neutral` (grey) when
 * further out, and `ink` once servis has them planned.
 */

import type { CalendarDelivery } from './api';
import type { Task } from '@/modules/ukoly/api';
import type { PlannedContact } from '@/modules/kontakty/api';
import type { OjetoTermin } from '@/modules/ojeta-vozidla/api';
import type { EventChipTone } from '@/components/EventChip';

export type KalendarView = 'mesic' | 'tyden';

export interface KalendarSearch {
  view?: KalendarView;
  mesic?: number;
  rok?: number;
  /** Anchor day for week view — ISO `YYYY-MM-DD`. Defaults to today. */
  den?: string;
}

const VALID_VIEWS = new Set<KalendarView>(['mesic', 'tyden']);

function isoDayShape(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  );
}

export function parseKalendarSearch(input: Record<string, unknown>): KalendarSearch {
  const out: KalendarSearch = {};
  if (typeof input.view === 'string' && VALID_VIEWS.has(input.view as KalendarView)) {
    out.view = input.view as KalendarView;
  }
  const mesic = typeof input.mesic === 'string' ? parseInt(input.mesic, 10) : NaN;
  const rok = typeof input.rok === 'string' ? parseInt(input.rok, 10) : NaN;
  if (Number.isFinite(mesic) && mesic >= 1 && mesic <= 12) out.mesic = mesic;
  if (Number.isFinite(rok) && rok >= 1900 && rok <= 2100) out.rok = rok;
  if (isoDayShape(input.den)) out.den = input.den;
  return out;
}

export const CESKY_MESIC = [
  '',
  'Leden',
  'Únor',
  'Březen',
  'Duben',
  'Květen',
  'Červen',
  'Červenec',
  'Srpen',
  'Září',
  'Říjen',
  'Listopad',
  'Prosinec',
] as const;

export const DNY_ZAHLAVI_FULL = [
  'Pondělí',
  'Úterý',
  'Středa',
  'Čtvrtek',
  'Pátek',
  'Sobota',
  'Neděle',
] as const;

export const DNY_ZAHLAVI_SHORT = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'] as const;

/** Returns local-date today's `YYYY-MM-DD`. */
export function todayIso(): string {
  const d = new Date();
  return isoDate(d);
}

export function isoDate(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}

/** Days in a given month (1-based month). */
export function daysInMonth(rok: number, mesic: number): number {
  return new Date(rok, mesic, 0).getDate();
}

/** 0-based Monday-anchored weekday index for the 1st of `rok`/`mesic`. */
export function firstWeekdayMondayBased(rok: number, mesic: number): number {
  const sunday0 = new Date(rok, mesic - 1, 1).getDay();
  return sunday0 === 0 ? 6 : sunday0 - 1;
}

/** Monday-anchored week containing the given ISO day (returns 7 ISO strings). */
export function weekDaysFor(anchorIso: string): string[] {
  const [y, m, d] = anchorIso.split('-').map((x) => parseInt(x, 10));
  const anchor = new Date(y, (m ?? 1) - 1, d ?? 1);
  const sunday0 = anchor.getDay();
  const mondayDelta = sunday0 === 0 ? -6 : 1 - sunday0;
  const monday = new Date(anchor);
  monday.setDate(anchor.getDate() + mondayDelta);
  const out: string[] = [];
  for (let i = 0; i < 7; i += 1) {
    const d2 = new Date(monday);
    d2.setDate(monday.getDate() + i);
    out.push(isoDate(d2));
  }
  return out;
}

/** Shifts an ISO day by `delta` calendar days. */
export function shiftIsoDays(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
  dt.setDate(dt.getDate() + delta);
  return isoDate(dt);
}

/** Format ISO `YYYY-MM-DD` → `j. M.` (e.g. 27. 4.). */
export function formatDayShort(iso: string): string {
  const [, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  return `${d}. ${m}.`;
}

/** Format ISO → `j. M. YYYY` (e.g. 27. 4. 2026). */
export function formatDayLong(iso: string): string {
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  return `${d}. ${m}. ${y}`;
}

export interface KalendarItem {
  kind: 'delivery' | 'task' | 'plan' | 'termin';
  /** Stable React key. */
  key: string;
  /**
   * Id of the record the chip opens: id_smlouvy, id_ukolu, id_kontaktu
   * (plan) or id_vozidla (termin).
   */
  id: number;
  title: string;
  sub?: string;
  /** Termín only — servis already has it booked (naplanovano). */
  planned?: boolean;
  /** Termín only — overdue or due within TERMIN_URGENT_DNU days. */
  urgent?: boolean;
  /** Termín only — nové (vozidla) or ojeté (ojeta-vozidla) stock; decides where the chip opens. */
  zdroj?: 'nove' | 'ojete';
}

/** Optional single-day overlays drawn on top of the deliveries. */
export interface KalendarOverlays {
  tasks?: Task[];
  plans?: PlannedContact[];
  terminy?: OjetoTermin[];
}

/** Sort rank within a day: deliveries, plans, tasks, deadlines. */
const KIND_RANK: Record<KalendarItem['kind'], number> = {
  delivery: 0,
  plan: 1,
  task: 2,
  termin: 3,
};

function compareItems(a: KalendarItem, b: KalendarItem): number {
  if (a.kind !== b.kind) return KIND_RANK[a.kind] - KIND_RANK[b.kind];
  return a.title.localeCompare(b.title, 'cs');
}

export function buildDeliveryItem(del: CalendarDelivery): KalendarItem {
  const car = [del.nazev_znacky, del.nazev_modelu, del.provedeni]
    .filter((x): x is string => Boolean(x?.trim()))
    .join(' ');
  return {
    kind: 'delivery',
    key: `s:${del.id_smlouvy}`,
    id: del.id_smlouvy,
    title: del.jmeno_zakaznika || 'Předání vozu',
    // Handed over → sits on the protocol date; mark it so it reads as done.
    sub: [del.predano ? 'Předáno' : '', car].filter(Boolean).join(' · ') || 'Předání',
  };
}

export function buildTaskItem(t: Task): KalendarItem {
  // Timed reminders lead the subtitle with HH:MM; whole-day ones show just the
  // assignee — or "soukromý" for a private task (the assignee is always me).
  const who = t.soukromy === 1 ? 'soukromý' : t.prijemce_jmeno;
  const sub = [t.cas_pripominky?.slice(0, 5), who].filter(Boolean).join(' · ');
  return {
    kind: 'task',
    key: `t:${t.id_ukolu}`,
    id: t.id_ukolu,
    title: t.nazev,
    sub: sub || undefined,
  };
}

export function buildPlanItem(p: PlannedContact): KalendarItem {
  // Timed plans lead the subtitle with HH:MM, like timed task reminders.
  const sub = [p.cas_planu?.slice(0, 5), p.plan_popis?.trim(), p.prodejce_jmeno?.trim()]
    .filter(Boolean)
    .join(' · ');
  return {
    kind: 'plan',
    // One kontakt can carry several active plans — key by the plan.
    key: `p:${p.id_planu ?? p.id_kontaktu}`,
    id: p.id_kontaktu,
    title: p.jmeno_klienta?.trim() || p.spolecnost?.trim() || 'Plánovaný kontakt',
    sub: sub || undefined,
  };
}

export const TERMIN_LABEL: Record<OjetoTermin['druh'], string> = {
  stk: 'STK',
  servis: 'Servisní prohlídka',
};

/** An unplanned deadline turns red once it's due within this many days. */
export const TERMIN_URGENT_DNU = 45;

export function buildTerminItem(t: OjetoTermin): KalendarItem {
  const car = [t.nazev_znacky, t.nazev_modelu].filter(Boolean).join(' ');
  const planned = t.naplanovano ? `Naplánováno na ${formatDayLong(t.naplanovano)}` : '';
  const sub = [planned, car, t.spz_vozu?.trim()].filter(Boolean).join(' · ');
  return {
    kind: 'termin',
    key: `${t.zdroj === 'nove' ? 'n' : 'o'}:${t.id_vozidla}:${t.druh}`,
    zdroj: t.zdroj ?? 'ojete',
    id: t.id_vozidla,
    title: TERMIN_LABEL[t.druh],
    sub: sub || undefined,
    planned: Boolean(t.naplanovano),
    urgent: t.dnu <= TERMIN_URGENT_DNU,
  };
}

export function itemTone(item: KalendarItem): EventChipTone {
  switch (item.kind) {
    case 'delivery':
      return 'ok';
    case 'task':
      return 'warn';
    case 'plan':
      return 'accent';
    case 'termin':
      if (item.planned) return 'ink';
      return item.urgent ? 'danger' : 'neutral';
  }
}

/**
 * Returns a `day-number → items[]` lookup for the given month.
 *
 * Every item pins to a single day. Items are sorted by `KIND_RANK`, then
 * alphabetically.
 */
export function bucketByDay(
  rok: number,
  mesic: number,
  deliveries: CalendarDelivery[],
  { tasks = [], plans = [], terminy = [] }: KalendarOverlays = {},
): Map<number, KalendarItem[]> {
  const dim = daysInMonth(rok, mesic);
  const map = new Map<number, KalendarItem[]>();

  for (const del of deliveries) {
    if (!del.datum_predani) continue;
    pushOnDay(map, del.datum_predani, rok, mesic, dim, buildDeliveryItem(del));
  }

  // Tasks pin to their single reminder day (datum_pripominky), like deliveries.
  for (const t of tasks) {
    if (!t.datum_pripominky) continue;
    pushOnDay(map, t.datum_pripominky, rok, mesic, dim, buildTaskItem(t));
  }

  for (const p of plans) {
    if (!p.datum_planu) continue;
    pushOnDay(map, p.datum_planu, rok, mesic, dim, buildPlanItem(p));
  }

  for (const t of terminy) {
    pushOnDay(map, t.datum, rok, mesic, dim, buildTerminItem(t));
  }

  for (const arr of map.values()) arr.sort(compareItems);
  return map;
}

/** Push `item` onto the day-bucket for `iso` when it falls inside rok/mesic. */
function pushOnDay(
  map: Map<number, KalendarItem[]>,
  iso: string,
  rok: number,
  mesic: number,
  dim: number,
  item: KalendarItem,
): void {
  const [yStr, mStr, dStr] = iso.split('-');
  const y = parseInt(yStr ?? '', 10);
  const m = parseInt(mStr ?? '', 10);
  const d = parseInt(dStr ?? '', 10);
  if (y === rok && m === mesic && d >= 1 && d <= dim) push(map, d, item);
}

function push<K, V>(map: Map<K, V[]>, key: K, value: V): void {
  const existing = map.get(key);
  if (existing) existing.push(value);
  else map.set(key, [value]);
}

/** Items for a single ISO day, drawn from the same month buckets. */
export function itemsForIsoDay(
  iso: string,
  deliveries: CalendarDelivery[],
  { tasks = [], plans = [], terminy = [] }: KalendarOverlays = {},
): KalendarItem[] {
  const out: KalendarItem[] = [];
  for (const del of deliveries) {
    if (del.datum_predani === iso) out.push(buildDeliveryItem(del));
  }
  for (const t of tasks) {
    if (t.datum_pripominky && t.datum_pripominky.slice(0, 10) === iso) out.push(buildTaskItem(t));
  }
  for (const p of plans) {
    if (p.datum_planu && p.datum_planu.slice(0, 10) === iso) out.push(buildPlanItem(p));
  }
  for (const t of terminy) {
    if (t.datum === iso) out.push(buildTerminItem(t));
  }
  out.sort(compareItems);
  return out;
}

/** Pick the month/year pair to query for a week-anchored view (use 1st-of-week). */
export function monthForWeek(weekDays: string[]): { mesic: number; rok: number } {
  // Use the middle day so a week straddling months still picks the "primary"
  // month — the API call is a single month, multi-month weeks would need two
  // queries; we accept the minor edge case (Thu picks the right month for any
  // 7-day window starting Mon).
  const mid = weekDays[3] ?? weekDays[0] ?? todayIso();
  const [y, m] = mid.split('-').map((x) => parseInt(x, 10));
  return { rok: y ?? new Date().getFullYear(), mesic: m ?? new Date().getMonth() + 1 };
}
